import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Package, Sparkles, Check, X, Users2, Folder, FileText, Archive, CreditCard, FileSpreadsheet, PackagePlus } from "lucide-react";

/**
 * Firm-side read-only "Your subscription" card. Rendered inside
 * FirmSettings → Subscription tab. Fetches:
 *   GET /workspace/subscription  → { subscription: {...}, usage: {...} }
 *
 * Includes live usage vs. the tier's limits (Team users X/Y, Projects A/B).
 */
export default function FirmSubscriptionCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/workspace/subscription");
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="card-soft flex items-center gap-2 text-[#7A7571]">
        <Loader2 size={16} className="animate-spin" /> Loading subscription…
      </div>
    );
  }

  if (!data?.subscription) {
    return (
      <div className="card-soft" data-testid="firm-subscription-none">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-[#F7F4EC] flex items-center justify-center text-[#7A7571]">
            <Package size={18} />
          </div>
          <div>
            <div className="font-display text-xl font-bold text-[#2C2A29]">No subscription assigned yet</div>
            <div className="text-sm text-[#7A7571]">Ask OrkaiFlow to activate your subscription.</div>
          </div>
        </div>
        <a
          href="mailto:sales@orkaiflow.com"
          className="btn-primary inline-flex items-center gap-2 mt-2"
          data-testid="contact-sales-btn"
        >
          Contact sales
        </a>
      </div>
    );
  }

  const s = data.subscription;
  const snap = s.snapshot || {};
  const limits = data.effective_limits || snap.limits || {};
  const usage = data.usage || {};
  const price = s.custom_price_cents != null
    ? formatPrice(snap.currency, s.custom_price_cents)
    : formatPrice(snap.currency, snap.price_monthly_cents);

  const enabledFeatures = (snap.features || []).filter((f) => f.enabled);
  const enabledAddons = (s.addons || []).filter((a) => a.enabled && a.quantity > 0);

  return (
    <div className="space-y-4" data-testid="firm-subscription-card">
      <div className="card-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Your subscription</div>
            <div className="font-display text-3xl font-bold text-[#2C2A29] mt-1" data-testid="firm-tier-name">{snap.name}</div>
            <div className="text-sm text-[#7A7571] mt-1">
              {price} <span className="text-[#B8B0A6]">·</span>{" "}
              <span className="capitalize">{s.billing_cycle}</span>
              {s.custom_price_cents != null && <span className="ml-2 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#FFEDD5] text-[#7C2D12] border border-[#FDBA74]">Custom price</span>}
            </div>
          </div>
          <div>
            <BillingMethodBadge method={s.billing_method} />
          </div>
        </div>

        {/* Subscription duration + renewal countdown */}
        {(data.subscription_end_date || data.renewal_status) && (
          <div
            className="mt-4 rounded-2xl bg-[#F7F4EC] p-3 flex items-center justify-between gap-3"
            data-testid="firm-subscription-duration"
          >
            <div className="text-xs text-[#7A7571]">
              <div className="uppercase tracking-widest font-bold text-[10px]">Subscription period</div>
              <div className="text-[#2C2A29] font-semibold mt-1" data-testid="firm-subscription-period">
                {formatDate(data.subscription_start_date)} &nbsp;→&nbsp; {formatDate(data.subscription_end_date)}
              </div>
            </div>
            {data.renewal_status && (
              <RenewalPill status={data.renewal_status} />
            )}
          </div>
        )}

        {/* Usage vs limits */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <UsageRow
            icon={<Users2 size={14} />}
            label="Team users"
            used={usage.team_users}
            limit={limits.max_team_users}
            testid="usage-team-users"
          />
          <UsageRow
            icon={<Folder size={14} />}
            label="Active projects"
            used={usage.active_projects}
            limit={limits.max_concurrent_projects}
            testid="usage-projects"
          />
          <UsageRow
            icon={<Archive size={14} />}
            label="Archived projects"
            used={usage.archived_projects}
            limit={limits.max_archived_projects}
            testid="usage-archived-projects"
          />
          <UsageRow
            icon={<FileText size={14} />}
            label="Docs / project"
            used={null}
            limit={limits.max_documents_per_project}
            testid="usage-docs"
          />
        </div>
      </div>

      {/* Add-ons */}
      {enabledAddons.length > 0 && (
        <div className="card-soft" data-testid="firm-addons-list">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-3 inline-flex items-center gap-1.5">
            <PackagePlus size={12} /> Active add-ons
          </div>
          <ul className="space-y-2">
            {enabledAddons.map((a) => {
              const bundleSize = Number(a.bundle_size || 1);
              const isBundled = bundleSize > 1;
              const bundleUnitWord = a.limit_key === "max_documents_per_project"
                ? "document"
                : a.limit_key === "max_archived_projects"
                ? "project"
                : "unit";
              const perUnitLabel = isBundled ? `per ${bundleSize}-${bundleUnitWord} bundle` : "per unit";
              return (
                <li key={a.addon_id} className="flex items-baseline justify-between gap-3 p-3 rounded-2xl bg-[#F7F4EC]" data-testid={`firm-addon-active-${a.addon_slug}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-[#2C2A29]">{a.name}</div>
                      {isBundled && (
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E40AF] border border-[#93C5FD]">
                          {bundleSize}-pack
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#7A7571]">
                      Qty <span className="font-mono">{a.quantity}</span>
                      {isBundled && (
                        <> (<span className="font-mono">{(a.quantity * bundleSize).toLocaleString()}</span> extra {bundleUnitWord}s)</>
                      )}
                      {" · "}
                      {a.currency} {(a.unit_price_cents / 100).toFixed(2)} {perUnitLabel}
                    </div>
                  </div>
                  <div className="text-sm font-display font-bold text-[#E07A5F] whitespace-nowrap">
                    {a.currency} {((a.monthly_total_cents || a.unit_price_cents * a.quantity) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    <span className="text-[10px] text-[#7A7571] ml-1">/mo</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Included features */}
      <div className="card-soft">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-3 inline-flex items-center gap-1.5">
          <Sparkles size={12} /> Included features
        </div>
        {enabledFeatures.length === 0 ? (
          <div className="text-sm text-[#7A7571] italic">No add-on features on this tier. Upgrade to unlock more.</div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {enabledFeatures.map((f) => (
              <li key={f.key} className="flex items-start gap-2 text-sm text-[#2C2A29]" data-testid={`firm-feature-${f.key}`}>
                <Check size={14} className="text-[#166534] flex-shrink-0 mt-0.5" />
                <span className="font-semibold">{humanizeFeatureKey(f.key)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {s.billing_method === "manual_invoice" && (
        <div className="card-soft" data-testid="firm-billing-manual-details">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-3 inline-flex items-center gap-1.5">
            <FileSpreadsheet size={12} /> Billing
          </div>
          <p className="text-sm text-[#4B4642] leading-relaxed">
            You&apos;re on <span className="font-semibold">manual invoicing</span>. Our sales team will issue you an invoice each billing cycle. Please wire payment to the bank account listed on your invoice.
          </p>
          {(s.billing_contact_name || s.billing_contact_email) && (
            <div className="mt-3 text-sm text-[#2C2A29]">
              Billing contact: {s.billing_contact_name || "—"}
              {s.billing_contact_email && <> · <a href={`mailto:${s.billing_contact_email}`} className="text-[#E07A5F] hover:underline">{s.billing_contact_email}</a></>}
            </div>
          )}
        </div>
      )}

      <div className="text-xs text-[#7A7571] italic">
        To upgrade, downgrade, or change your billing details, email{" "}
        <a href="mailto:sales@orkaiflow.com" className="text-[#E07A5F] hover:underline">sales@orkaiflow.com</a>.
      </div>
    </div>
  );
}

function BillingMethodBadge({ method }) {
  if (method === "stripe") {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#DBEAFE] text-[#1E40AF] text-xs font-semibold">
        <CreditCard size={12} /> Stripe
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FEF3C7] text-[#78350F] text-xs font-semibold">
      <FileSpreadsheet size={12} /> Manual invoice
    </span>
  );
}

function UsageRow({ icon, label, used, limit, testid }) {
  const unlimited = limit == null;
  const usageStr = used == null
    ? (unlimited ? "Unlimited" : Number(limit).toLocaleString())
    : (unlimited ? `${used} / Unlimited` : `${used} / ${limit}`);
  const pct = !unlimited && used != null && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const overQuota = !unlimited && used != null && used > limit;
  return (
    <div className="rounded-2xl bg-[#F7F4EC] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] flex items-center gap-1">{icon} {label}</div>
      <div className={`text-sm font-semibold mt-1 ${overQuota ? "text-[#9F1239]" : "text-[#2C2A29]"}`}>
        {usageStr}{overQuota && <span className="ml-1"><X size={11} className="inline text-[#9F1239]" /></span>}
      </div>
      {!unlimited && used != null && limit > 0 && (
        <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${overQuota ? "bg-[#9F1239]" : pct > 80 ? "bg-[#E07A5F]" : "bg-[#81B29A]"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

function humanizeFeatureKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function RenewalPill({ status }) {
  const cat = status?.category;
  const dl = status?.days_left;
  const map = {
    expired:       { label: "Expired",                              bg: "#FEE2E2", fg: "#9F1239" },
    renewal_soon:  { label: `Renewal in ${dl}d`,                    bg: "#FEF3C7", fg: "#92400E" },
    active:        { label: dl != null ? `${dl}d left` : "Active",  bg: "#D1FAE5", fg: "#065F46" },
    unknown:       { label: "No end date set",                      bg: "#F1EFEA", fg: "#4B4642" },
  };
  const style = map[cat] || map.unknown;
  return (
    <span
      className="text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full whitespace-nowrap"
      style={{ backgroundColor: style.bg, color: style.fg }}
      data-testid="firm-subscription-renewal-pill"
    >
      {style.label}
    </span>
  );
}

export function formatPrice(currency, cents) {
  if (cents == null) return "Custom pricing";
  if (cents === 0) return "Free";
  const amount = cents / 100;
  return `${currency || "SGD"} ${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/mo`;
}
